// Scenario lifecycle helpers shared across the scheduling workspace.
// Server status is always the source of truth (ASSESSMENT.md P1: "ตัดสิน solve จาก returned
// scenario.status; ไม่แจ้ง success จาก HTTP 200 เพียงอย่างเดียว").

export const SCENARIO_STATUS = {
  DRAFT: 'DRAFT',
  SOLVING: 'SOLVING',
  SOLVED: 'SOLVED',
  INFEASIBLE: 'INFEASIBLE',
  STOCK_ONLY: 'STOCK_ONLY',
  NO_DEMAND: 'NO_DEMAND',
  APPROVED: 'APPROVED',
  PUBLISHED: 'PUBLISHED',
  ERROR: 'ERROR',
  CANCELLED: 'CANCELLED',
};

export const STATUS_LABEL = {
  DRAFT: 'Draft',
  SOLVING: 'Solving…',
  SOLVED: 'Solved',
  INFEASIBLE: 'Infeasible',
  STOCK_ONLY: 'Stock only (no fresh demand)',
  NO_DEMAND: 'No demand',
  APPROVED: 'Approved',
  PUBLISHED: 'Published',
  ERROR: 'Error',
  CANCELLED: 'Cancelled',
  SUPERSEDED: 'Superseded',
  EMPTY: 'Empty',
};

export const STATUS_COLOR = {
  DRAFT: 'default',
  SOLVING: 'info',
  SOLVED: 'success',
  INFEASIBLE: 'error',
  STOCK_ONLY: 'warning',
  NO_DEMAND: 'warning',
  APPROVED: 'success',
  PUBLISHED: 'primary',
  ERROR: 'error',
  CANCELLED: 'default',
  EMPTY: 'default',
};

// Which lifecycle action is legal from a given status. Mirrors backend transitions described in
// ASSESSMENT.md / IMPLEMENTATION_PLAN.md: DRAFT -solve-> SOLVED|INFEASIBLE|STOCK_ONLY|NO_DEMAND|ERROR,
// SOLVED -approve-> APPROVED, APPROVED -publish-> PUBLISHED, PUBLISHED -reconcile/reoptimize->.
export function canRunAction(status, action) {
  switch (action) {
    case 'solve':
      return status === SCENARIO_STATUS.DRAFT;
    case 'approve':
      return status === SCENARIO_STATUS.SOLVED;
    case 'publish':
      return status === SCENARIO_STATUS.APPROVED;
    case 'reconcile':
      return status === SCENARIO_STATUS.PUBLISHED;
    case 'reoptimize':
      return status === SCENARIO_STATUS.PUBLISHED;
    case 'cancel':
      return [
        SCENARIO_STATUS.DRAFT,
        SCENARIO_STATUS.SOLVED,
        SCENARIO_STATUS.INFEASIBLE,
        SCENARIO_STATUS.APPROVED,
      ].includes(status);
    default:
      return false;
  }
}

// Merge a scenario's own batches with any preserved_batches carried over from a prior revision.
// ASSESSMENT.md P1: "ตารางแสดงเฉพาะ selectedScenario.batches" caused preserved work to disappear.
// Dedupe by the underlying database batch id (a preserved batch may appear in both collections
// depending on backend serialization) and tag preserved rows so the UI can mark them read-only /
// show their source scenario.
export function mergeScenarioBatches(scenario) {
  if (!scenario) return [];
  const batches = Array.isArray(scenario.batches) ? scenario.batches : [];
  const preserved = Array.isArray(scenario.preserved_batches) ? scenario.preserved_batches : [];

  const byId = new Map();
  batches.forEach((batch) => {
    byId.set(batch.id, { ...batch, isPreserved: Boolean(batch.is_preserved || batch.preserved) });
  });
  preserved.forEach((batch) => {
    const existing = byId.get(batch.id);
    byId.set(batch.id, {
      ...(existing || {}),
      ...batch,
      isPreserved: true,
      sourceScenarioId: batch.source_scenario_id ?? batch.scenario ?? existing?.sourceScenarioId ?? null,
    });
  });

  return Array.from(byId.values()).sort((a, b) => {
    const aStart = a.planned_start ? new Date(a.planned_start).getTime() : 0;
    const bStart = b.planned_start ? new Date(b.planned_start).getTime() : 0;
    return aStart - bStart;
  });
}

export function scenarioLabel(scenario) {
  if (!scenario) return '';
  const start = scenario.planning_start ? String(scenario.planning_start).replace('T', ' ').slice(0, 16) : '';
  return `#${scenario.id} · ${STATUS_LABEL[scenario.status] || scenario.status} · ${start}`;
}
